import React, {
  useEffect,
  useState,
  useMemo,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useRef,
} from "react";
import api from "../../../api";
import { toast } from "react-toastify";

import {
  Card,
  CardBody,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";

import OpNumSelector from "./OpNumSelector";
import DnaNumDetails from "./DnaNumDetails";
import DnaDetailsHeader from "./DnaDetailsHeader";
import DynamicCommentGrid from "./DynamicCommentGrid";
import OutOfSpecModal from "./OutOfSpecModal";
import "../di-theme.css";
import { usePermissions } from "../../../hooks/usePermissions"; // Round DI-1
import useAuth from "../../Personal Attendance/useAuth";
import CloseOpModal from "./CloseOpModal";
import ProblemsModal from "./ProblemsModal";
import FrmSupvOv from "./FrmSupvOv";
import SupvOverrideModal from "./SupvOverrideModal";
import AttachmentsPanel from "./AttachmentsPanel";

import { useDynamicBoxes } from "./useDynamicBoxes";
import { useResolvedEntryMap } from "./useResolvedEntryMap";

// Generic confirm modal (used for Close Op confirm + Close Job confirm).
// Reactstrap rewrite — matches the rest of the dashboard's modal style
// (CloseOpModal, OutOfSpecModal, ProblemsModal, FrmSupvOv all use Reactstrap).
// Behavior is unchanged: open/title/message/onCancel/onConfirm props.
const ConfirmModal = ({ open, title, message, onCancel, onConfirm }) => {
  return (
    <Modal isOpen={!!open} toggle={onCancel} centered backdrop="static">
      <ModalHeader toggle={onCancel}>
        <i className="ri-question-line align-bottom me-2 text-primary"></i>
        {title || "Confirm"}
      </ModalHeader>
      <ModalBody>
        <div className="text-muted">{message}</div>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onCancel}>
          No
        </Button>
        <Button color="primary" onClick={onConfirm}>
          <i className="ri-check-line align-bottom me-1"></i>
          Yes
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const InspectionSectionInner = (
  {
    jobNum,
    waveNumber,
    revMajor,
    revMinor,
    seq,
    lotNum,
    enteredBy,
    partNum,
    quantity,
    originalpart,
    onDnaDisplayChange,
    // Legacy MDIParent1 status-strip parity: lblEngineer ("Created By
    // Engineer: ...") came from mafiaTable.Rows(0).createdBy. Reported
    // up so the page header can show it next to MAFIA Rev / Quantity.
    onMetaChange,
    onCloseActionsStateChange,
    // Batch 3 / Round 15 — simulator preview mode. Legacy Simselect.vb
    // opened the inspection with Panel1.Enabled = False and engineer
    // mode skipped InsertHeader/saves. readOnly=true disables every
    // entry box, the comment, Save, and the close actions.
    readOnly = false,
    // Round 22 — Part Number LAZY HEADER: true when the run has no
    // MafiaJobHeader row yet (opened but never saved). The box layout is
    // then driven by the `quantity` prop directly (no original-quantity
    // lookup — there is no header to read), and every save carries
    // PartOnlyHeaderQuantity so the FIRST save creates the header.
    partHeaderPending = false,
  },
  ref
) => {
  const [fullData, setFullData] = useState([]);
  const originalPartNum = originalpart;

  const [extraLabels, setExtraLabels] = useState([]);
  const [selectedOpNum, setSelectedOpNum] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedDna, setSelectedDna] = useState(null);

  const [entryValues, setEntryValues] = useState({});
  const [payloadComment, setPayloadComment] = useState("");
  // Round DI-2 (Issue 15) — true only when the operator actually edited
  // the comment this session (typing or Date & Initial Stamp). A comment
  // merely LOADED from the database is not "unsaved work", so leaving the
  // DNA without touching it doesn't trigger a needless save.
  const [commentDirty, setCommentDirty] = useState(false);

  // Round DI-2.1 — "[YYYY-MM-DD HH:mm II] " stamp text from the
  // AUTHENTICATED user. Fallback chain: display-name initials → username →
  // "EmpID <n>" (clearly labeled). Returns "" when no identity can be
  // resolved — callers must refuse to stamp rather than stamp "??" or
  // anyone else's identity. Shared by the Date & Initial Stamp button and
  // the automatic first-keystroke stamp.
  const buildStamp = useCallback(() => {
    let who = "";
    try {
      const au = JSON.parse(sessionStorage.getItem("authUser") || "{}");
      const nm = (au?.data?.name || au?.name || "").trim();
      if (nm) who = nm.split(/\s+/).filter(Boolean).map((w) => w[0]).join("").toUpperCase();
      else {
        const uname = (au?.data?.username || au?.username || "").trim();
        const empId = au?.empID ?? au?.empId ?? au?.data?.empID ?? au?.data?.empId ?? "";
        who = uname || (empId ? `EmpID ${empId}` : "");
      }
    } catch {}
    if (!who) return "";
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `[${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())} ${who}] `;
  }, []);

  const [unresolvedCells, setUnresolvedCells] = useState([]);
  const [showOutOfSpecModal, setShowOutOfSpecModal] = useState(false);
  // Round DI-3 — the attachments panel registers its file-picker opener
  // here so the page-level "Upload File" button can trigger it directly.
  const openUploadPickerRef = useRef(null);

  const [mafiaEntryTable, setMafiaEntryTable] = useState([]);

  const [opClosed, setOpClosed] = useState(false);
  const [jobClosed, setJobClosed] = useState(false);
  const [showCloseOpModal, setShowCloseOpModal] = useState(false);

  const [showProblemsModal, setShowProblemsModal] = useState(false);
  const [problemsList, setProblemsList] = useState([]);

  const [extraEntries, setExtraEntries] = useState([]);

  // Batch 2 / Round 9 — legacy "!" DNA markers (Inspect.vb failFlag).
  // Set of DNA numbers that contain a failing entry; DnaNumDetails
  // renders the "!" suffix for display only.
  const [failedDnas, setFailedDnas] = useState(() => new Set());

  const [loadingMafiaTable, setLoadingMafiaTable] = useState(false);
  const [loadingDnaDetails, setLoadingDnaDetails] = useState(false);
  const [savingEntries, setSavingEntries] = useState(false);
  // Round DI-2 (Issue 3) — small save-status indicator for the auto-save:
  // 'idle' | 'saved' | 'failed'. "Saving…" is derived from savingEntries;
  // "Saved" self-clears after a few seconds; "Save failed" persists until
  // the next attempt so the operator notices.
  const [lastSaveStatus, setLastSaveStatus] = useState("idle");
  const saveStatusTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(saveStatusTimerRef.current), []);
  const [closingOp, setClosingOp] = useState(false);
  const [closingJob, setClosingJob] = useState(false);

  const isInProcType = Number(waveNumber) === 1;

  // Close-Op confirm
  const [showConfirmCloseOp, setShowConfirmCloseOp] = useState(false);
  const [confirmCloseOpMsg, setConfirmCloseOpMsg] = useState("");

  // Close-Job confirm
  const [showConfirmCloseJob, setShowConfirmCloseJob] = useState(false);

  // Supervisor override (frmSupvOv)
  const [showSupvOv, setShowSupvOv] = useState(false);
  const [supvPcsRemaining, setSupvPcsRemaining] = useState(0);
  const [supvNonConfs, setSupvNonConfs] = useState(0);
  const pendingCloseJobRef = useRef(null);

  const [showSupvOverrideModal, setShowSupvOverrideModal] = useState(false);
  const [supvOverrideCtx, setSupvOverrideCtx] = useState(null);
  // Round 5 (explicit override entry point): toggles a small piece picker
  // so allowed users can launch Supervisor Override from a BUTTON, not only
  // by double-clicking a label. Both paths call openSupvOverrideForCell.
  const [showOvrPicker, setShowOvrPicker] = useState(false);
  // Cleanup: Documents panel is hidden by default and opened from a button
  // in the action bar, so it no longer sits as a big block at the bottom.
  const [showDocs, setShowDocs] = useState(false);
  // Names (txtDynamic{N}) that were saved in THIS session. A box is locked
  // (no delete, supervisor-only) if it's here OR in disabledFields. This is
  // reliable immediately after save without depending on the DB cell-number
  // round-trip. Cleared when the DNA changes.
  const [savedThisSession, setSavedThisSession] = useState(() => new Set());
  // Names a supervisor deleted this session. Kept so a deleted box can never
  // reappear as an empty shell from a stale reconstruction. Cleared on DNA
  // change (handleDnaSelect resets all box state).
  const [deletedNames, setDeletedNames] = useState(() => new Set());

  const mafiaLoadSeqRef = useRef(0);
  const dnaLoadSeqRef = useRef(0);
  const requestCacheRef = useRef(new Map());

  // Round DI-2 (Issue 13) — whether "File DMR" can actually create the
  // Epicor Nonconformance right now (config-gated). Used by the
  // Nonconformity modal to warn + confirm the local-only path. Defaults
  // pessimistically to false; a fetch failure just keeps the warning on.
  const [epicorDmrEnabled, setEpicorDmrEnabled] = useState(false);
  useEffect(() => {
    let alive = true;
    api
      .get("/Inspection/nonconf-config")
      .then((res) => {
        if (alive) setEpicorDmrEnabled(!!res?.data?.epicorDmrEnabled);
      })
      .catch(() => {
        /* keep false — the modal will warn conservatively */
      });
    return () => {
      alive = false;
    };
  }, []);

  const stableStringify = useCallback((value) => {
    const sortObject = (obj) => {
      if (Array.isArray(obj)) return obj.map(sortObject);
      if (obj && typeof obj === "object") {
        return Object.keys(obj)
          .sort()
          .reduce((acc, key) => {
            acc[key] = sortObject(obj[key]);
            return acc;
          }, {});
      }
      return obj;
    };
    return JSON.stringify(sortObject(value));
  }, []);

  const cachedPost = useCallback(
    async (url, payload, { useCache = true } = {}) => {
      if (!useCache) {
        return api.post(url, payload);
      }

      const key = `${url}::${stableStringify(payload || {})}`;
      if (requestCacheRef.current.has(key)) {
        return requestCacheRef.current.get(key);
      }

      const promise = api.post(url, payload).catch((err) => {
        requestCacheRef.current.delete(key);
        throw err;
      });

      requestCacheRef.current.set(key, promise);
      return promise;
    },
    [stableStringify]
  );

  const clearReadCache = useCallback(() => {
    requestCacheRef.current.clear();
  }, []);

  // Build mafiaTables lookup
  const mafiaTables = useMemo(() => {
    const map = {};
    for (const item of fullData) {
      if (item.DNANum) map[item.DNANum] = item;
    }
    return map;
  }, [fullData]);

  // Load inspection table
  useEffect(() => {
    const getInspectionData = async () => {
      const seqNo = ++mafiaLoadSeqRef.current;
      setLoadingMafiaTable(true);

      try {
        const res = await cachedPost(
          "/Inspection/mafiatable",
          {
            partnumber: partNum,
            major: revMajor,
            minor: revMinor,
          },
          { useCache: true }
        );

        if (seqNo !== mafiaLoadSeqRef.current) return;
        setFullData(res.data || []);
        try {
          const rows0 = (res.data || [])[0];
          const raw = String(rows0?.createdBy ?? "").trim().replace(/\./g, " ");
          const engineer = raw
            .split(/\s+/)
            .filter(Boolean)
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
            .join(" ");
          onMetaChange?.({ engineer });
        } catch {
          onMetaChange?.({ engineer: "" });
        }
      } catch {
        if (seqNo !== mafiaLoadSeqRef.current) return;
        toast.error("Failed to load inspection data.");
      } finally {
        if (seqNo === mafiaLoadSeqRef.current) {
          setLoadingMafiaTable(false);
        }
      }
    };

    getInspectionData();
  }, [partNum, revMajor, revMinor, cachedPost]);

  // Filter based on selected op
  useEffect(() => {
    if (selectedOpNum === null) return;

    const result =
      selectedOpNum === "All"
        ? fullData
        : selectedOpNum === "Uncategorized"
          ? fullData.filter((d) => !d.MOMOperationNum)
          : fullData.filter((d) => d.MOMOperationNum === selectedOpNum);

    setFilteredData(result);
    setSelectedDna(null);
    setEntryValues({});
    setPayloadComment("");
    setCommentDirty(false); // Round DI-2 — comment reloads with the DNA
    setUnresolvedCells([]);
    setExtraEntries([]);
    setExtraLabels([]);
    setMafiaEntryTable([]);
    setOpClosed(false);
    setJobClosed(false);

    if (onDnaDisplayChange) onDnaDisplayChange("");
  }, [selectedOpNum, fullData, onDnaDisplayChange]);

  const refreshFailFlags = useCallback(async () => {
    try {
      const res = await api.post("/Inspection/dna-fail-flags", {
        JobNum: String(jobNum),
        RevMajor: Number(revMajor),
        RevMinor: Number(revMinor),
        WaveNumber: Number(waveNumber),
        Seq: Number(seq),
        PartNum: String(partNum),
        LotNum: Number(lotNum || 0),
        OrgPartNum: String(originalPartNum),
      });
      const list = Array.isArray(res?.data) ? res.data : [];
      setFailedDnas(new Set(list.map((d) => String(d).trim())));
    } catch {
      // non-fatal: markers simply don't show
    }
  }, [jobNum, revMajor, revMinor, waveNumber, seq, partNum, lotNum, originalPartNum]);

  useEffect(() => {
    // Legacy parity: markers computed when the DNA list is (re)filled.
    if ((filteredData || []).length > 0) refreshFailFlags();
    else setFailedDnas(new Set());
  }, [filteredData, refreshFailFlags]);

  const { entryCount, entryLabels, entryNames, jobQuantity } = useDynamicBoxes({
    jobNum,
    waveNumber,
    seq,
    partNum,
    lotNum,
    revMajor,
    revMinor,
    selectedDna,
    mafiaTables,
    originalPartNum,
    // Round 15: simulator preview — fake job 12345 has no MafiaJobHeader
    // row, so the legacy fixed quantity (the `quantity` prop, 500) drives
    // the box layout directly. Round 22: same mechanism for a part-mode
    // run whose header is pending first save. 0 in normal mode =
    // unchanged behavior.
    presetQuantity:
      readOnly || partHeaderPending ? Number(quantity) || 0 : 0,
  });

  const { values: prefillValues, disabledFields, highlightedFields } =
    useResolvedEntryMap({
      mafiaEntryTable,
      selectedDna,
      min: selectedDna?.Min,
      max: selectedDna?.Max,    });

  useEffect(() => {
    // Merge: DB-prefilled (saved) values take precedence for their own keys,
    // but values entered/kept this session are preserved (so a just-saved
    // extra box keeps showing its value even before the DB round-trip
    // re-supplies it). Pure replace previously blanked extra boxes.
    setEntryValues((prev) => ({ ...prev, ...prefillValues }));
  }, [prefillValues]);

  // ROOT-CAUSE FIX: reconstruct SAVED added (+N) boxes from the database
  // rows. Added boxes were previously kept only in local state, so after a
  // reload (or in another browser) a saved +N box rendered under a fresh
  // local name that did NOT match its DB row — so it never showed as
  // "saved" (it kept the × and a normal user could remove it). Here we read
  // them straight from mafiaEntryTable: any row whose cell is NOT a base
  // piece is an added box. Its name is txtDynamic{CellNum} (matching the DB
  // and disabledFields), and its label is "+{pieceNumber-100000}".
  const savedExtraEntries = useMemo(() => {
    if (!Array.isArray(mafiaEntryTable) || mafiaEntryTable.length === 0)
      return [];
    const baseCellNums = new Set(
      (entryNames || [])
        .map((nm) => {
          const m = String(nm).match(/^txtDynamic(\d+)$/);
          return m ? Number(m[1]) : null;
        })
        .filter((x) => x != null)
    );
    const out = [];
    const seen = new Set();
    for (const row of mafiaEntryTable) {
      const cell = row?.CellNum != null ? Number(row.CellNum) : null;
      if (cell == null || !Number.isFinite(cell)) continue;
      if (baseCellNums.has(cell)) continue; // base piece, not an added box
      const name = `txtDynamic${cell}`;
      if (seen.has(name)) continue;
      seen.add(name);
      const pn = row?.pieceNumber != null ? Number(row.pieceNumber) : null;
      const label =
        Number.isFinite(pn) && pn >= 100000 ? `+${pn - 100000}` : `+${cell}`;
      out.push({ name, label });
    }
    // Stable order by the +N number.
    out.sort((a, b) => {
      const na = Number(String(a.label).replace("+", "")) || 0;
      const nb = Number(String(b.label).replace("+", "")) || 0;
      return na - nb;
    });
    return out;
  }, [mafiaEntryTable, entryNames]);

  // The boxes actually rendered: saved added boxes (from DB) first, then any
  // unsaved added boxes from this session that aren't already represented.
  const mergedExtraEntries = useMemo(() => {
    const byName = new Map();
    for (const e of savedExtraEntries) byName.set(e.name, e);
    for (const e of extraEntries) if (!byName.has(e.name)) byName.set(e.name, e);
    // Drop anything a supervisor deleted this session so no empty shell lingers.
    for (const n of deletedNames) byName.delete(n);
    return [...byName.values()];
  }, [savedExtraEntries, extraEntries, deletedNames]);

  const mergedExtraLabels = useMemo(
    () => mergedExtraEntries.map((e) => e.label),
    [mergedExtraEntries]
  );

  // After a supervisor deletes a saved value, the box may live in the BASE
  // arrays (useDynamicBoxes bakes saved extra rows into entryNames/Labels via
  // the server "extras" count) rather than in extraEntries. Filter deleted
  // names out of the base arrays too, so no empty shell remains until the
  // next full reload re-derives the counts.
  const visibleBase = useMemo(() => {
    const names = [];
    const labels = [];
    (entryNames || []).forEach((nm, i) => {
      if (deletedNames.has(nm)) return;
      names.push(nm);
      labels.push((entryLabels || [])[i]);
    });
    return { names, labels, count: names.length };
  }, [entryNames, entryLabels, deletedNames]);

  const isSpecialDim = (txt) => (txt || "").toLowerCase().includes("*");
  const isPassFailMethod = (txt) => (txt || "").toLowerCase().includes("fail");

  // Legacy special PF cells
  const passFailSpecialCells = new Set([
    "500010",
    "500015",
    "500018",
    "500022",
    "500025",
    "500026",
    "500027",
  ]);

  // ✅ Accept "p" or "pass" (also "f"/"fail") everywhere
  const normalizePF = (val) => {
    const v = (val ?? "").toString().trim().toLowerCase();
    if (v === "p" || v === "pass") return "p";
    if (v === "f" || v === "fail") return "f";
    return v;
  };

  // Convert PF to legacy DB codes
  const pfToCode = (val) => {
    const v = normalizePF(val);
    if (v === "p") return "-777";
    if (v === "f") return "-999";
    return val;
  };

  const toBool = (v) => {
    if (v === true || v === false) return v;
    if (v == null) return false;
    const s = String(v).trim().toLowerCase();
    return s === "1" || s === "true" || s === "y" || s === "yes";
  };

  const normalizeCloseResp = (data) => {
    if (data && typeof data === "object") {
      const candidate =
        data.isClosed ??
        data.closed ??
        data.IsClosed ??
        data.QualityClosed ??
        data.value ??
        data.result ??
        data.data;
      return toBool(candidate);
    }
    return toBool(data);
  };

  // Batch 3 / Round 17 — legacy-verbatim validation sentences
  // (Inspect.vb TextBox_Leave), prefixed with the piece label so a
  // multi-cell list stays readable:
  //   • "P or F must be entered for Pass/Fail dimensions.  Please
  //     re-enter value."
  //   • "Non-numeric value detected.  Please re-enter value."
  //   • "Invalid date detected. Please enter date in format of:
  //     12/25/2013 " (date cells — also enforced at blur)
  const buildProblemMessages = (unresolvedCellNums) => {
    if (!selectedDna) return [];

    const nameToLabel = new Map();
    (entryNames || []).forEach((nm, i) => nameToLabel.set(nm, entryLabels?.[i]));
    extraEntries.forEach((e) => nameToLabel.set(e.name, e.label));

    const spec =
      filteredData.find((d) => d.DNANum === selectedDna?.DNANum) || {};
    const method = spec.MethodOfRecording || "";
    const dimType = (spec.DimType || "").toLowerCase();

    const msgs = [];

    for (const cell of unresolvedCellNums) {
      const name = `txtDynamic${cell}`;
      const label = nameToLabel.get(name) ?? cell;

      const isDateCell =
        dimType.includes("*") && (cell === 500001 || cell === 500023);
      const isPFContext =
        isPassFailMethod(method) ||
        (isSpecialDim(dimType) && passFailSpecialCells.has(String(cell)));

      if (isDateCell) {
        msgs.push(
          `Piece ${label}: Invalid date detected. Please enter date in format of:  12/25/2013 `
        );
      } else if (isPFContext) {
        msgs.push(
          `Piece ${label}: P or F must be entered for Pass/Fail dimensions.  Please re-enter value.`
        );
      } else {
        msgs.push(
          `Piece ${label}: Non-numeric value detected.  Please re-enter value.`
        );
      }
    }
    return msgs;
  };

  const buildDnaBundlePayload = useCallback(
    (item) => {
      const jobOp = `${jobNum?.toString().trim()}${(item?.MOMOperationNum ?? "")
        .toString()
        .trim()}`;

      return {
        JobNum: String(jobNum),
        DnaNum: String(item.DNANum),
        RevMajor: Number(revMajor),
        RevMinor: Number(revMinor),
        WaveNumber: Number(waveNumber),
        Seq: Number(seq),
        PartNum: String(item.PartNum),
        LotNum: Number(lotNum || 0),
        JobQuan: Number(quantity),
        OrgPartNum: String(originalPartNum),
        JobOp: jobOp,
      };
    },
    [jobNum, revMajor, revMinor, waveNumber, seq, lotNum, quantity, originalPartNum]
  );

  const applyDnaBundle = useCallback((bundle) => {
    setMafiaEntryTable(bundle?.mafiaEntryTable || []);
    setPayloadComment(bundle?.comment === "NULL" ? "" : bundle?.comment || "");
    setCommentDirty(false); // Round DI-2 — DB comment is the saved state
    setOpClosed(normalizeCloseResp(bundle?.opClosed));
    setJobClosed(normalizeCloseResp(bundle?.jobClosed));
  }, []);

  const refreshCurrentDna = useCallback(
    async (dnaItem) => {
      if (!dnaItem) return;
      try {
        const res = await cachedPost(
          "/Inspection/dna-bundle",
          buildDnaBundlePayload(dnaItem),
          { useCache: false }
        );
        applyDnaBundle(res?.data || {});
      } catch {
        // silent
      }
    },
    [cachedPost, buildDnaBundlePayload, applyDnaBundle]
  );

  // Round DI-1 — auto-save (owner: "records inserted in the previous DNA
  // auto save when moved onto the next DNA", comments included). On switch
  // with unsaved work: clean → silent save then switch; skipped/format
  // problems or unclassified out-of-spec values → the switch is BLOCKED
  // with the same messages the old Save button used (nothing is lost).
  const hasUnsavedWork = useCallback(() => {
    if (readOnly) return false;
    const disabledSet = new Set(disabledFields || []);
    const dirtyEntries = Object.entries(entryValues).some(
      ([k, v]) => !disabledSet.has(k) && (v ?? "").toString().trim() !== ""
    );
    // Round DI-2 (Issue 15): only an EDITED comment counts as unsaved work.
    return dirtyEntries || commentDirty;
  }, [readOnly, disabledFields, entryValues, commentDirty]);

  // Round DI-2 (bug fix — TDZ + stale closures): the DI-1 version of the
  // auto-save was a useCallback whose DEPENDENCY ARRAY referenced
  // proceedToSave — a const declared ~140 lines further down — so the
  // array's render-time evaluation threw "Cannot access 'proceedToSave'
  // before initialization" as soon as the workspace mounted. It also
  // captured mount-time state in handleDnaSelect (selectedDna stayed null
  // in the closure forever), so the auto-save never ran even conceptually.
  //
  // The fix: everything reads the auto-save through autoSaveRef, which is
  // re-published on EVERY render with the latest closure. The function
  // itself (autoSaveBeforeLeave) is a plain function defined AFTER
  // proceedToSave — no useCallback, no deps array, no TDZ. selectedDnaRef
  // mirrors the latest selected DNA the same way.
  const autoSaveRef = useRef(async () => true);
  const selectedDnaRef = useRef(null);
  useEffect(() => {
    selectedDnaRef.current = selectedDna;
  }, [selectedDna]);

  const handleDnaSelect = useCallback(
    async (item) => {
      // Round DI-1/DI-2 — auto-save the DNA being left before switching
      // (via refs so the CURRENT DNA and entries are seen, not mount-time
      // state). Blocked switches keep the current DNA selected and keep
      // every entered value.
      const current = selectedDnaRef.current;
      if (current && item !== current) {
        const ok = await autoSaveRef.current();
        if (!ok) return; // blocked — keep the current DNA selected
      }
      setSelectedDna(item || null);

      setEntryValues({});
      setPayloadComment("");
      setCommentDirty(false); // Round DI-2 — fresh DNA, clean comment
      setUnresolvedCells([]);
      setExtraEntries([]);
      setExtraLabels([]);
      setMafiaEntryTable([]);
      setSavedThisSession(new Set());
      setDeletedNames(new Set());

      if (onDnaDisplayChange) {
        onDnaDisplayChange(item?.DNANum ? String(item.DNANum) : "");
      }

      try {
        const scroller = document.querySelector("[data-inspection-scroll]");
        if (scroller) scroller.scrollTop = 0;
      } catch { }

      if (!item) return;

      const seqNo = ++dnaLoadSeqRef.current;
      setLoadingDnaDetails(true);

      try {
        const res = await cachedPost(
          "/Inspection/dna-bundle",
          buildDnaBundlePayload(item),
          { useCache: true }
        );

        if (seqNo !== dnaLoadSeqRef.current) return;
        applyDnaBundle(res?.data || {});
      } catch {
        if (seqNo !== dnaLoadSeqRef.current) return;
        toast.error("Failed to load inspection detail.");
      } finally {
        if (seqNo === dnaLoadSeqRef.current) {
          setLoadingDnaDetails(false);
        }
      }
    },
    [onDnaDisplayChange, cachedPost, buildDnaBundlePayload, applyDnaBundle]
  );

  const closedAllDisabledNames = useMemo(() => {
    if (!selectedDna) return [];
    const shouldDisableAll =
      readOnly || (isInProcType && opClosed) || (!isInProcType && jobClosed);
    if (!shouldDisableAll) return [];
    const extras = extraEntries.map((e) => e.name);
    return [...new Set([...(entryNames || []), ...extras])];
  }, [selectedDna, entryNames, extraEntries, isInProcType, opClosed, jobClosed, readOnly]);

  // Batch 2 / Round 11 — legacy GetUnresolvedNoncomformities +
  // MafiaDB.IsOutOfSpec parity, with the user-approved fix that a
  // typed "F" counts as a fail (the legacy check compared against the
  // "-999" sentinel that was only substituted later in the save loop —
  // an ordering bug; the intent of the check is restored here per the
  // Phase 1 decision "F requires the nonconformity flow").
  // Rules (legacy): only ENABLED cells, only cellNum < 500001 (special
  // 500xxx boxes are excluded from nonconformity detection), non-empty:
  //   • pass/fail method OR "*" dimType → F / FAIL / -999 = nonconformity
  //   • numeric methods → value outside Min/Max (single-sided bounds
  //     honored, as in IsOutOfSpec) = nonconformity
  // Format errors (non-numeric, not-P/F) are NOT nonconformities — they
  // route through the Problems flow (legacy TextBox_Leave clearing).
  const getUnresolvedNonconformities = () => {
    if (!selectedDna) return [];

    const spec = filteredData.find((d) => d.DNANum === selectedDna.DNANum);
    if (!spec) return [];

    const method = spec.MethodOfRecording || "";
    const dimType = (spec.DimType || "").toLowerCase();
    const min = parseFloat(spec.Min);
    const max = parseFloat(spec.Max);

    const disabledSet = new Set(disabledFields || []);
    const out = [];

    for (const [name, raw] of Object.entries(entryValues)) {
      if (!raw || !raw.toString().trim()) continue;
      if (disabledSet.has(name)) continue; // prefilled rows are not re-evaluated

      const cellNum = parseInt(name.replace("txtDynamic", ""), 10);
      if (!Number.isFinite(cellNum)) continue;
      if (cellNum >= 500001) continue; // legacy: idx < 500001 only

      const isPF = isPassFailMethod(method) || isSpecialDim(dimType);
      if (isPF) {
        const v = normalizePF(raw);
        if (v === "f" || raw.toString().trim() === "-999") out.push(cellNum);
        continue;
      }

      // Round DI-3 (owner): a typed F is a FAIL in EVERY cell type —
      // numeric (Min/Max) methods included — and routes through the same
      // nonconformity flow as an out-of-range number; P is a pass. The
      // legacy parity rules only honored P/F on pass/fail-method cells,
      // so operators typing F on a caliper dimension got the format-
      // problem clear instead of a nonconformance.
      {
        const pv = normalizePF(raw);
        if (pv === "f" || raw.toString().trim() === "-999") { out.push(cellNum); continue; }
        if (pv === "p") continue;
      }
      const num = Number(raw);
      if (Number.isNaN(num)) continue; // format error → Problems flow
      if (!Number.isNaN(min) && num < min) out.push(cellNum);
      else if (!Number.isNaN(max) && num > max) out.push(cellNum);
    }

    return out;
  };

  // UAT 14-16: now RETURNS true/false so the out-of-spec confirm can keep
  // its modal (and the inspection context) open when the save fails.
  const proceedToSave = async (nonConformities = null) => {
    if (readOnly) return false; // Round 15: simulator preview never writes
    if (!selectedDna) {
      toast.error("No DNA selected.");
      return false;
    }
    if (savingEntries) return false;

    setSavingEntries(true);

    // Round 10 (legacy insert-only): prefilled/disabled cells are
    // existing DB rows — they are NEVER re-submitted (legacy skipped
    // con.Enabled = False boxes). Only new values go to the server.
    const disabledSet = new Set(disabledFields || []);
    const entries = Object.entries(entryValues)
      .filter(([k, val]) => !disabledSet.has(k) && (val ?? "").toString().trim() !== "")
      .reduce((acc, [k, v]) => {
        let newVal = v;
        if (
          isSpecialDim(selectedDna?.DimType) ||
          isPassFailMethod(selectedDna?.MethodOfRecording)
        ) {
          newVal = pfToCode(v);
        }
        return { ...acc, [k]: (newVal ?? "").toString().trim() };
      }, {});

    const payload = {
      jobNum,
      waveNumber,
      revMajor,
      revMinor,
      dnaNum: selectedDna.DNANum,
      // DATA-PARITY FIX: legacy saved lblTool.Text — which is the
      // MafiaTables "SpecialGages" column ("Gage to use") — into
      // MafiaEntries.Tool. The previous code read a nonexistent "Tool"
      // column, so every save stored a blank tool.
      tool: selectedDna.SpecialGages || selectedDna.Tool || "",
      assemblySeq: seq,
      partNum: selectedDna.PartNum,
      orgPartNum: originalPartNum,
      lotNum,
      enteredBy,
      entries,
      comment: payloadComment,
      nonConformities,
      labels: [...entryLabels, ...extraLabels],
      // Round 10 — legacy Data-Integrity check: how many existing rows
      // this screen loaded for the DNA (= dna-bundle row count). The
      // server re-counts and 409s with the legacy message if someone
      // else inserted rows in the meantime.
      knownEntryCount: (mafiaEntryTable || []).length,
      // Round 22 — Part Number LAZY HEADER: while the run's header is
      // pending, every save carries the quantity; the server creates the
      // header on the first save (IF NOT EXISTS — idempotent afterwards).
      // undefined for job/PO/lot flows and for already-saved runs.
      partOnlyHeaderQuantity: partHeaderPending
        ? Number(quantity) || 0
        : undefined,
    };

    try {
      const res = await api.post("/Inspection/save-inspection", payload);
      if (res.status === 200) {
        // Round DI-2 — status pill: Saved (auto-clears after 4s).
        setLastSaveStatus("saved");
        clearTimeout(saveStatusTimerRef.current);
        saveStatusTimerRef.current = setTimeout(
          () => setLastSaveStatus("idle"),
          4000
        );
        toast.success("Entries saved successfully.");
        // Lock the boxes we just saved: they can no longer be deleted by
        // Clear/×; only a supervisor override can change them.
        const justSaved = Object.keys(entries);
        if (justSaved.length > 0) {
          setSavedThisSession((prev) => {
            const next = new Set(prev);
            justSaved.forEach((n) => next.add(n));
            return next;
          });
        }
        setUnresolvedCells([]);
        setCommentDirty(false); // Round DI-2 — comment now persisted
        clearReadCache();
        await refreshCurrentDna(selectedDna);
        refreshFailFlags(); // Round 9: a saved FAIL should mark the DNA with "!"
        return true;
      } else {
        setLastSaveStatus("failed"); // Round DI-2 — status pill
        toast.error("Failed to save entries.");
        return false;
      }
    } catch (err) {
      setLastSaveStatus("failed"); // Round DI-2 — status pill
      // Round 10: the server now sends the legacy-verbatim message in
      // the 409 body — "someone else has added to this PIR" (Data
      // Integrity) or the closed-job/closed-operation text. Show it
      // as-is instead of a generic placeholder.
      const serverMsg = err?.response?.data?.error;
      if (err?.response?.status === 409 && serverMsg) {
        toast.error(serverMsg);
      } else if (err?.response?.status === 409) {
        toast.error(
          "Error - someone else has added to this PIR while you were in this screen.  Please close this screen and try again. "
        );
      } else if (serverMsg) {
        toast.error(serverMsg);
      } else {
        toast.error("Server error during save.");
      }
      return false;
    } finally {
      setSavingEntries(false);
    }
  };

  // Group-start cells of the special 500xxx configs, exempt from the
  // skip-entry rule exactly as in Inspect.vb — used by BOTH the blur-time
  // check (handleFieldBlur) and the save-time check (getSkippedEntryNames).
  const SPECIAL_GROUP_STARTS = useMemo(
    () =>
      new Map([
        [500001, "check w/ gauge*"],
        [500003, "heat treat*"],
        [500007, "weld*"],
        [500011, "passivation*"],
        [500016, "humidity chamber*"],
        [500020, "salt fog*"],
        [500023, "copper sulfate*"],
        [500026, "final inspection*"],
        [500028, "pass/fail*"],
      ]),
    []
  );

  // ── UAT Test 11 — skip-entry rule, re-validated at SAVE time ──
  // The on-blur check (handleFieldBlur) can be bypassed (e.g. pasting,
  // clicking Save while focus is still in the box, or clearing an
  // earlier box after a later one was filled). This applies the SAME
  // legacy Inspect.vb rule to every rendered box right before save:
  // a filled box whose previous cell exists on screen but is blank is
  // a skipped entry. Cell 1 and the special 500xxx group-start cells
  // are exempt, exactly as in the blur check / legacy code.
  const getSkippedEntryNames = () => {
    if (!selectedDna) return [];
    const dimType = (selectedDna?.DimType || "").toLowerCase().trim();

    const allNamesArr = [
      ...(visibleBase.names || []),
      ...mergedExtraEntries.map((e) => e.name),
    ];
    const allNames = new Set(allNamesArr);
    const skipped = [];

    for (const name of allNamesArr) {
      const raw = (entryValues[name] ?? "").toString().trim();
      if (!raw) continue;

      const cellNum = parseInt(String(name).replace("txtDynamic", ""), 10);
      if (!Number.isFinite(cellNum)) continue;

      const isGroupStart =
        SPECIAL_GROUP_STARTS.has(cellNum) &&
        SPECIAL_GROUP_STARTS.get(cellNum) === dimType;
      const prevIndex = cellNum - 1;
      if (prevIndex === 0 || isGroupStart) continue;

      const prevName = `txtDynamic${prevIndex}`;
      if (!allNames.has(prevName)) continue;

      const prevVal = (entryValues[prevName] ?? "").toString().trim();
      if (prevVal === "") skipped.push(name);
    }

    return skipped;
  };

  const handleSave = () => {
    // Batch 2 / Round 11 — legacy save order:
    //   0. UAT Test 11: skipped entries → clear the later box(es), show
    //      "Please do not skip an entry." and abort the save so the
    //      blur-time check cannot be bypassed.
    //   1. Format problems (non-numeric, not-P/F) → Problems modal and
    //      the offending cells are CLEARED (legacy TextBox_Leave).
    //   2. Genuine nonconformities (typed F / FAIL, numeric value out
    //      of Min/Max) → frmOutOfSpec equivalent: severity + course of
    //      action are MANDATORY before the save proceeds. Cancelling
    //      aborts the save (legacy e.Cancel = True).
    //   3. Otherwise save immediately.
    const skipped = getSkippedEntryNames();
    if (skipped.length > 0) {
      setEntryValues((prev) => {
        const next = { ...prev };
        for (const nm of skipped) {
          if (next[nm] != null) next[nm] = "";
        }
        return next;
      });
      toast.error("Please do not skip an entry.");
      return;
    }

    const { hasProblems } = checkAndShowProblems();
    if (hasProblems) return;

    const unresolved = getUnresolvedNonconformities();
    if (unresolved.length > 0) {
      setUnresolvedCells(unresolved);
      setShowOutOfSpecModal(true);
    } else {
      proceedToSave();
    }
  };

  // Batch 2 / Round 9 — legacy TextBox_Leave rules, applied on blur:
  //   • Date cells: 500001 (check w/ gauge*) and 500023 (copper
  //     sulfate*) must parse as a date AND contain exactly two "/"
  //     (legacy slashCount check). Invalid → cell cleared + legacy
  //     message "Invalid date detected. Please enter date in format
  //     of:  12/25/2013".
  //   • Skip rule: an entry may not be filled while the previous cell
  //     is still empty ("Please do not skip an entry."). Group-start
  //     cells of the special 500xxx configs and cell 1 are exempt,
  //     exactly as in Inspect.vb. (SPECIAL_GROUP_STARTS is declared
  //     above getSkippedEntryNames, which shares it.)
  const handleFieldBlur = (name) => {
    const raw = (entryValues[name] ?? "").toString().trim();
    const cellNum = parseInt(String(name).replace("txtDynamic", ""), 10);
    const dimType = (selectedDna?.DimType || "").toLowerCase().trim();

    if (raw && Number.isFinite(cellNum)) {
      // 1. Legacy date validation (only for *-dims, cells 500001/500023)
      if (
        dimType.includes("*") &&
        (cellNum === 500001 || cellNum === 500023)
      ) {
        const parsed = Date.parse(raw);
        const slashCount = raw.split("/").length - 1;
        if (Number.isNaN(parsed) || slashCount !== 2) {
          setEntryValues((prev) => ({ ...prev, [name]: "" }));
          toast.error(
            "Invalid date detected. Please enter date in format of:  12/25/2013 "
          );
          return;
        }
      }

      // 2. Legacy skip-entry rule
      const isGroupStart =
        SPECIAL_GROUP_STARTS.has(cellNum) &&
        SPECIAL_GROUP_STARTS.get(cellNum) === dimType;
      const prevIndex = cellNum - 1;
      if (prevIndex !== 0 && !isGroupStart) {
        const prevName = `txtDynamic${prevIndex}`;
        const allNames = new Set([
          ...(entryNames || []),
          ...extraEntries.map((x) => x.name),
        ]);
        if (allNames.has(prevName)) {
          const prevVal = (entryValues[prevName] ?? "").toString().trim();
          if (prevVal === "") {
            setEntryValues((prev) => ({ ...prev, [name]: "" }));
            toast.error("Please do not skip an entry.");
            return;
          }
        }
      }
    }

    // 3. Existing per-blur format check (P/F, numeric) — unchanged.
    checkAndShowProblems();
  };

  const getProblemCells = () => {
    if (!selectedDna) return [];

    const spec = filteredData.find((d) => d.DNANum === selectedDna.DNANum);
    if (!spec) return [];

    const method = spec.MethodOfRecording || "";
    const dimType = (spec.DimType || "").toLowerCase();

    const out = [];

    for (const [name, raw] of Object.entries(entryValues)) {
      if (raw == null || !raw.toString().trim()) continue;

      const cellNum = parseInt(name.replace("txtDynamic", ""), 10);
      if (!Number.isFinite(cellNum)) continue;

      if (isSpecialDim(dimType) && passFailSpecialCells.has(String(cellNum))) {
        const v = normalizePF(raw);
        if (!(v === "p" || v === "f")) out.push(cellNum);
        continue;
      }

      if (isPassFailMethod(method)) {
        const v = normalizePF(raw);
        if (!(v === "p" || v === "f")) out.push(cellNum);
        continue;
      }

      // Round DI-3 — P/F are valid entries in numeric cells too (see the
      // nonconformity detector above); they are no longer format problems.
      {
        const pv = normalizePF(raw);
        if (pv === "p" || pv === "f" || raw.toString().trim() === "-999") continue;
      }
      const num = Number(raw);
      if (Number.isNaN(num)) {
        out.push(cellNum);
        continue;
      }
    }

    return out;
  };

  const getOutOfSpecCells = () => {
    if (!selectedDna) return [];

    const spec = filteredData.find((d) => d.DNANum === selectedDna.DNANum);
    if (!spec) return [];

    const method = spec.MethodOfRecording || "";
    const dimType = (spec.DimType || "").toLowerCase();
    const min = parseFloat(spec.Min);
    const max = parseFloat(spec.Max);

    const outCells = [];

    for (const [name, raw] of Object.entries(entryValues)) {
      if (!raw || !raw.toString().trim()) continue;

      const cellNum = parseInt(name.replace("txtDynamic", ""), 10);
      if (!Number.isFinite(cellNum)) continue;

      if (isSpecialDim(dimType) && passFailSpecialCells.has(String(cellNum))) {
        continue;
      }
      if (isPassFailMethod(method)) continue;

      const num = Number(raw);
      if (Number.isNaN(num)) continue;

      if (!Number.isNaN(min) && num < min)
        outCells.push({ cellNum, value: num });
      else if (!Number.isNaN(max) && num > max)
        outCells.push({ cellNum, value: num });
    }

    return outCells;
  };

  const checkAndShowProblems = () => {
    const problemCells = getProblemCells();
    if (problemCells.length === 0) {
      setUnresolvedCells([]);
      return { hasProblems: false, problemCells };
    }

    setProblemsList(buildProblemMessages(problemCells));
    setShowProblemsModal(true);
    setUnresolvedCells(problemCells);

    setEntryValues((prev) => {
      const next = { ...prev };
      for (const cell of problemCells) {
        const name = `txtDynamic${cell}`;
        if (next[name] != null) next[name] = "";
      }
      return next;
    });

    return { hasProblems: true, problemCells };
  };

  // ── Round DI-2 — the DNA/Op-leave auto-save (Issue 3) ──
  // Plain function, deliberately NOT memoized: it is re-created every
  // render with the latest state and published through autoSaveRef below,
  // which is the ONLY way it is invoked (handleDnaSelect, the Op switch,
  // flushAutoSave, Close Inspection). Defined here, after every function
  // it calls (proceedToSave / getSkippedEntryNames / checkAndShowProblems /
  // getUnresolvedNonconformities), so there is no render-time TDZ — the
  // DI-1 useCallback version listed proceedToSave in its dependency array
  // before that const initialized and crashed the workspace on mount.
  const autoSaveBeforeLeave = async () => {
    if (!hasUnsavedWork()) return true;
    const skipped = getSkippedEntryNames();
    if (skipped.length > 0) {
      toast.error(
        "Please do not skip an entry — complete or clear it before moving on."
      );
      return false;
    }
    const { hasProblems } = checkAndShowProblems();
    if (hasProblems) {
      toast.error("Please fix the highlighted entries before moving on.");
      return false;
    }
    // Out-of-spec values OPEN the Nonconformity modal (legacy
    // frmOutOfSpec) so the operator can classify right away — the
    // DNA/Op switch stays blocked until the classification is confirmed
    // and saved. (DI-1 called an undefined function name here.)
    const ncCells = getUnresolvedNonconformities();
    if (ncCells.length > 0) {
      setUnresolvedCells(ncCells);
      setShowOutOfSpecModal(true);
      toast.info(
        "Out-of-spec entries need a severity and action before moving on."
      );
      return false;
    }
    const ok = await proceedToSave();
    if (!ok)
      toast.error("Auto-save failed — your entries were kept; please retry.");
    return ok;
  };
  autoSaveRef.current = autoSaveBeforeLeave;

  const outSpecCellsForHighlight = getOutOfSpecCells();
  const outSpecNames = outSpecCellsForHighlight.map(
    (c) => `txtDynamic${c.cellNum}`
  );

  // --- Close Operation eligibility (legacy: in-process + numeric op selected)
  const canShowCloseOp =
    isInProcType && selectedOpNum && typeof selectedOpNum === "number";

  // ✅ send close state to header whenever it changes
  useEffect(() => {
    if (!onCloseActionsStateChange) return;
    onCloseActionsStateChange({
      canCloseOp: !readOnly && !!canShowCloseOp,
      opClosed: !!opClosed,
      canCloseJob: !readOnly && !isInProcType,
      jobClosed: !!jobClosed,
    });
  }, [
    onCloseActionsStateChange,
    canShowCloseOp,
    opClosed,
    isInProcType,
    jobClosed,
    readOnly,
  ]);

  const onConfirmCloseOp = async ({ pass, fail }) => {
    if (!selectedDna || !canShowCloseOp || closingOp) return;

    setClosingOp(true);
    try {
      const res = await api.post("/Inspection/close-operation", {
        JobNum: String(jobNum),
        PartNum: String(selectedDna.PartNum),
        orgPartNum: originalPartNum,
        RevMin: Number(revMinor),
        RevMaj: Number(revMajor),
        Wave: Number(waveNumber),
        OpNum: String(selectedOpNum),
        NumPass: Number(pass),
        NumFail: Number(fail),
        Seq: Number(seq),
        Lot: Number(lotNum || 0),
        Employee: String(enteredBy || ""),
      });

      if (res?.status === 200 && res?.data === true) {
        toast.success("Operation closed.");
        clearReadCache();
        await refreshCurrentDna(selectedDna);
        setShowCloseOpModal(false);
      } else {
        toast.error(
          "Could not close Operation -- has this operation already been closed?  Please double check and try again."
        );
      }
    } catch (err) {
      // Go-live parity (2026-09-24): show the server's reason when it sends
      // one (e.g. a job / part number too long for the op-close record).
      toast.error(err?.response?.data?.error || "Error closing operation.");
    } finally {
      setClosingOp(false);
    }
  };

  const saveIsDisabled =
    readOnly ||
    !selectedDna ||
    loadingDnaDetails ||
    savingEntries ||
    (Number(entryCount || 0) + extraEntries.length) === 0 ||
    (isInProcType && opClosed) ||
    (!isInProcType && jobClosed);

  const parseCellFromName = useCallback((nm) => {
    const m = String(nm || "").match(/^txtDynamic(\d+)$/);
    return m ? Number(m[1]) : null;
  }, []);

  const computeNextExtraMeta = useCallback(
    (tempExtra = extraEntries, tempLabels = extraLabels) => {
      const n = tempLabels.length;
      let nextLabelNumber = -1;

      if (n === 0) {
        const labelNumbers = (entryLabels || [])
          .filter((label) => String(label).startsWith("+"))
          .map((label) => parseInt(String(label).replace("+", ""), 10))
          .filter((x) => Number.isFinite(x));

        nextLabelNumber = Math.max(0, ...labelNumbers, 0) + 1;
      } else {
        nextLabelNumber =
          parseInt(String(tempLabels.at(n - 1)).replace("+", ""), 10) + 1;
      }

      const nextLabel = `+${String(nextLabelNumber)}`;

      const baseCells = (entryNames || [])
        .map(parseCellFromName)
        .filter((x) => Number.isFinite(x));

      const extraCells = (tempExtra || [])
        .map((e) => parseCellFromName(e.name))
        .filter((x) => Number.isFinite(x));

      // Bug fix: also account for SAVED cells (disabledFields names like
      // "txtDynamic{N}"). After deleting added boxes, extraCells shrinks; if
      // we computed maxCell from baseCells+extraCells only, the next Inspect
      // All would REUSE a cell number that is already saved, and the re-added
      // box would render greyed-out/locked. Including saved cells here means
      // new boxes always get a fresh, higher cell number.
      const savedCells = [...(disabledFields || [])]
        .map(parseCellFromName)
        .filter((x) => Number.isFinite(x));

      if (
        baseCells.length === 0 &&
        extraCells.length === 0 &&
        savedCells.length === 0
      ) {
        return { txtDynamicName: "txtDynamic500001", label: nextLabel };
      }

      const maxCell = Math.max(
        ...baseCells,
        ...(extraCells.length ? extraCells : [0]),
        ...(savedCells.length ? savedCells : [0])
      );
      const nextCell =
        Number.isFinite(maxCell) && maxCell > 0 ? maxCell + 1 : 500001;

      return {
        txtDynamicName: `txtDynamic${nextCell}`,
        label: nextLabel,
      };
    },
    [entryLabels, extraEntries, extraLabels, entryNames, parseCellFromName, disabledFields]
  );

  const addOneMorePiece = useCallback(() => {
    if (!selectedDna) {
      toast.error("Select a DNA first.");
      return;
    }

    const { txtDynamicName, label } = computeNextExtraMeta(
      extraEntries,
      extraLabels
    );

    setExtraEntries((prev) => [...prev, { name: txtDynamicName, label }]);
    setExtraLabels((prev) => [...prev, label]);
    setEntryValues((prev) =>
      prev[txtDynamicName] != null ? prev : { ...prev, [txtDynamicName]: "" }
    );
  }, [selectedDna, computeNextExtraMeta, extraEntries, extraLabels]);

  // Cleanup: reverse "Inspect All" / remove boxes — but ONLY unsaved boxes.
  // A box whose value is already persisted shows up in `disabledFields`
  // (prefilled + locked from the MAFIA entry table). Those must NOT be
  // removed by Clear/×; only a supervisor override can change a saved value.
  // This also fixes the grey-out-on-re-add bug: saved cells are never
  // dropped, so re-adding never collides with a locked cell.
  const savedNameSet = useMemo(
    () => new Set([
      ...(disabledFields || []),
      ...closedAllDisabledNames,
      ...savedThisSession,
    ]),
    [disabledFields, closedAllDisabledNames, savedThisSession]
  );

  // Count of added boxes that are still UNSAVED — this is what "Clear added"
  // can actually remove. Saved added boxes are excluded so the button never
  // implies it will delete saved data (it won't).
  const unsavedExtraCount = useMemo(
    () => extraEntries.filter((e) => !savedNameSet.has(e.name)).length,
    [extraEntries, savedNameSet]
  );

  const clearAddedBoxes = useCallback(() => {
    // Only added boxes that are NOT saved are removable.
    const removable = extraEntries.filter((e) => !savedNameSet.has(e.name));
    if (removable.length === 0) {
      toast.info("No unsaved added boxes to clear.");
      return;
    }
    const removableNames = new Set(removable.map((e) => e.name));

    setExtraEntries((prev) => prev.filter((e) => !removableNames.has(e.name)));
    setExtraLabels((prevLabels) => {
      // Rebuild labels aligned to the surviving extras.
      const survivors = extraEntries
        .filter((e) => !removableNames.has(e.name))
        .map((e) => e.label);
      return survivors;
    });
    setEntryValues((prev) => {
      const next = { ...prev };
      removableNames.forEach((n) => {
        delete next[n];
      });
      return next;
    });

    const keptSaved = extraEntries.length - removable.length;
    toast.success(
      `Removed ${removable.length} unsaved box(es)` +
        (keptSaved > 0 ? `; kept ${keptSaved} saved box(es).` : ".")
    );
  }, [extraEntries, savedNameSet]);

  const removeBox = useCallback(
    (name) => {
      // Guard: never remove a saved (locked) box. Saved values require a
      // supervisor override to change.
      if (savedNameSet.has(name)) {
        toast.error(
          "This value is already saved. A supervisor override is required to change it."
        );
        return;
      }
      setExtraEntries((prev) => {
        const idx = prev.findIndex((e) => e.name === name);
        if (idx === -1) return prev; // only ADDED boxes are removable
        const next = [...prev];
        next.splice(idx, 1);
        setExtraLabels((prevLabels) => {
          const nl = [...prevLabels];
          if (idx < nl.length) nl.splice(idx, 1);
          return nl;
        });
        setEntryValues((prevVals) => {
          const nv = { ...prevVals };
          delete nv[name];
          return nv;
        });
        return next;
      });
    },
    [savedNameSet]
  );

  // Round 7 — SUPERVISOR delete of a SAVED box. Unlike removeBox (frontend
  // only, unsaved boxes), this calls the backend to DELETE the MafiaEntries
  // row from the database, then removes the box from the UI. Supervisor-only
  // (the button is gated, and the server re-validates the permission).
  const deleteSavedEntry = useCallback(
    async (name, label) => {
      if (readOnly) return;
      if (!selectedDna) return;
      if (!isAllowedSupvOverrideUser()) {
        toast.error("A supervisor is required to delete a saved value.");
        return;
      }

      const cellNum = parseCellNumFromName(name);
      if (!Number.isFinite(cellNum)) {
        toast.error("Could not resolve the cell for this box.");
        return;
      }
      const pieceNum = parsePieceNumFromLabel(label);

      const ok = window.confirm(
        "Delete this SAVED inspection value from the database? This cannot be undone."
      );
      if (!ok) return;

      try {
        const res = await api.post("/Inspection/delete-mafia-entry", {
          JobNum: String(jobNum),
          DnaNum: String(selectedDna.DNANum),
          RevMajor: Number(revMajor),
          RevMinor: Number(revMinor),
          WaveNumber: Number(waveNumber),
          CellNum: Number(cellNum),
          PieceNumber: Number.isFinite(pieceNum) ? Number(pieceNum) : 0,
          Seq: Number(seq),
          PartNum: String(selectedDna.PartNum),
          Lot: Number(lotNum || 0),
          OrgPartNum: String(originalPartNum),
        });

        if (res.status === 200 && res.data) {
          toast.success("Saved value deleted.");
          // Remove from UI: drop the extra box (if it is one), clear value,
          // and un-track it from the saved sets so it isn't shown locked.
          setExtraEntries((prev) => prev.filter((e) => e.name !== name));
          setExtraLabels((prev) => {
            // rebuild labels aligned to surviving extras
            return prev; // labels are re-derived from extraEntries on next render path
          });
          setEntryValues((prev) => {
            const nv = { ...prev };
            delete nv[name];
            return nv;
          });
          setSavedThisSession((prev) => {
            const next = new Set(prev);
            next.delete(name);
            return next;
          });
          setDeletedNames((prev) => {
            const next = new Set(prev);
            next.add(name);
            return next;
          });
          // Reload from DB so disabledFields/prefill reflect the deletion.
          clearReadCache();
          await refreshCurrentDna(selectedDna);
          refreshFailFlags();
        } else {
          toast.error("Delete did not affect any row (already removed?).");
        }
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data ||
          "Failed to delete saved value.";
        toast.error(String(msg));
      }
    },
    [
      readOnly,
      selectedDna,
      jobNum,
      revMajor,
      revMinor,
      waveNumber,
      seq,
      lotNum,
      originalPartNum,
      clearReadCache,
      refreshCurrentDna,
      refreshFailFlags,
    ]
  );

  // Batch 2 / Round 9 — legacy AddAll parity: "Inspect All" fills the
  // grid up to the JOB QUANTITY (legacy: addlBoxes = Quantity −
  // ScanLast()), replacing the previous fixed 21-box cap.
  const inspectAllToQuantity = useCallback(() => {
    if (!selectedDna) {
      toast.error("Select a DNA first.");
      return;
    }

    const target = Number(jobQuantity || 0) > 0 ? Number(jobQuantity) : Number(quantity || 0);
    if (!Number.isFinite(target) || target <= 0) {
      toast.error("Job quantity unavailable — cannot Inspect All.");
      return;
    }

    const base = Number(entryCount || 0);
    const currentTotal = base + extraEntries.length;

    if (currentTotal >= target) {
      toast.info(`Already showing ${currentTotal} boxes (job quantity is ${target}).`);
      return;
    }

    const toAdd = target - currentTotal;
    const nextExtras = [...extraEntries];
    const nextLabels = [...extraLabels];
    const patch = {};

    for (let i = 0; i < toAdd; i++) {
      const { txtDynamicName, label } = computeNextExtraMeta(
        nextExtras,
        nextLabels
      );
      nextExtras.push({ name: txtDynamicName, label });
      nextLabels.push(label);
      patch[txtDynamicName] = "";
    }

    setExtraEntries(nextExtras);
    setExtraLabels(nextLabels);
    setEntryValues((prev) => {
      const merged = { ...prev };
      for (const [k, v] of Object.entries(patch)) {
        if (merged[k] == null) merged[k] = v;
      }
      return merged;
    });

    toast.success(`Inspect All: added ${toAdd} box(es) (total ${target}).`);
  }, [selectedDna, jobQuantity, quantity, entryCount, extraEntries, extraLabels, computeNextExtraMeta]);

  const goPrevDna = useCallback(() => {
    if (!selectedDna) return toast.info("Select a DNA first.");
    const list = filteredData || [];
    const idx = list.findIndex((d) => d?.DNANum === selectedDna?.DNANum);
    if (idx <= 0) return toast.info("No previous DNA.");
    handleDnaSelect(list[idx - 1]);
  }, [selectedDna, filteredData, handleDnaSelect]);

  const goNextDna = useCallback(() => {
    if (!selectedDna) return toast.info("Select a DNA first.");
    const list = filteredData || [];
    const idx = list.findIndex((d) => d?.DNANum === selectedDna?.DNANum);
    if (idx < 0 || idx >= list.length - 1) return toast.info("No next DNA.");
    handleDnaSelect(list[idx + 1]);
  }, [selectedDna, filteredData, handleDnaSelect]);

  // --------------------------------------------------------
  // ✅ LEGACY Close Op flow with aggregated prep endpoint
  // --------------------------------------------------------
  const legacyCloseOpFlow = useCallback(async () => {
    if (readOnly) return; // Round 15: simulator preview
    if (!canShowCloseOp) {
      toast.error(
        "Close Operation is only available for in-process jobs with a selected operation."
      );
      return;
    }
    if (opClosed) {
      toast.info("Operation is already closed.");
      return;
    }

    try {
      const dnaList = (filteredData || [])
        .map((d) => String(d?.DNANum || "").replace("!", "").trim())
        .filter(Boolean);

      if (dnaList.length === 0) {
        toast.error("No DNA list available to close.");
        return;
      }

      const resp = await api.post("/Inspection/prepare-close-operation", {
        JobNum: String(jobNum),
        RevMajor: Number(revMajor),
        RevMinor: Number(revMinor),
        WaveNumber: 1,
        Seq: Number(seq),
        PartNum: String(partNum),
        LotNum: Number(lotNum || 0),
        OrgPartNum: String(originalPartNum),
        JobQuan: Number(quantity),
        DnaNums: dnaList,
      });

      const remaining = Number(resp?.data?.remainingPieces ?? 0);

      if (remaining > 0) {
        setConfirmCloseOpMsg(
          `There are ${remaining} pieces that have not been entered. Are you sure you wish to close this operation?`
        );
        setShowConfirmCloseOp(true);
        return;
      }

      setShowCloseOpModal(true);
    } catch {
      toast.error("Failed to prepare Close Operation.");
    }
  }, [
    canShowCloseOp,
    opClosed,
    filteredData,
    quantity,
    revMajor,
    revMinor,
    partNum,
    jobNum,
    seq,
    lotNum,
    originalPartNum,
  ]);

  // Round DI-2 — role flags no longer drive any override visibility; the
  // gate below is purely permission-based. useAuth() kept for its session
  // side effects only.
  useAuth();
  // Round DI-1 — Supervisor Override is now PERMISSION-driven
  // ("DI - Supervisor Override", gate di.supervisorOverride): grant it per
  // ROLE or per PERSON in Access Management; IT Admin retains access via
  // the gate's fallback. The hardcoded role check is gone.
  const { canAccessModule } = usePermissions();
  const isAllowedSupvOverrideUser = useCallback(
    () => !!(canAccessModule && canAccessModule("di.supervisorOverride")),
    [canAccessModule]
  );

  const parseCellNumFromName = (nm) => {
    const m = String(nm || "").match(/^txtDynamic(\d+)$/);
    return m ? Number(m[1]) : null;
  };

  const parsePieceNumFromLabel = (lbl) => {
    const s = String(lbl ?? "").trim();
    if (s.includes("+")) {
      const numPart = s.split("+")[1];
      return (100000 + Number(numPart)).toString();
    }
    const cellNum = Number(s);
    if (!isNaN(cellNum)) {
      return s;
    }
    const m = s.match(/\d+/);
    return m ? (100000 + Number(m[0])).toString() : "100000";
  };

  const openSupvOverrideForCell = useCallback(
    async (cellName, pieceLabel) => {
      if (readOnly) return; // Round 15: simulator preview
      if (!selectedDna) return;
      if (!isAllowedSupvOverrideUser()) return;

      const cellNum = parseCellNumFromName(cellName);
      if (!Number.isFinite(cellNum)) return;

      const pieceNum = parsePieceNumFromLabel(pieceLabel);

      const spec =
        filteredData.find((d) => d?.DNANum === selectedDna?.DNANum) || {};
      const dimType = (spec?.DimType || "").toString();
      const method = (spec?.MethodOfRecording || "").toString().toLowerCase();
      const type = dimType.includes("*") ? "*" : method;

      try {
        const res = await api.post("/Inspection/get-cell", {
          JobNum: String(jobNum),
          DnaNum: String(selectedDna.DNANum),
          RevMajor: Number(revMajor),
          RevMinor: Number(revMinor),
          WaveNumber: Number(waveNumber),
          CellNum: Number(cellNum),
          Seq: Number(seq),
          PartNum: String(selectedDna.PartNum),
          LotNum: Number(lotNum || 0),
          OrgPartNum: String(originalPartNum),
        });

        const currentValue = (res?.data ?? "").toString();

        if (!currentValue || currentValue.trim() === "") {
          toast.error("There is not an existing value to override.");
          return;
        }

        setSupvOverrideCtx({
          jobNum,
          dnaNum: selectedDna.DNANum,
          pieceNum: pieceNum,
          revMajor,
          revMinor,
          waveNumber,
          cellNum,
          type,
          currentValue,
        });
        setShowSupvOverrideModal(true);
      } catch {
        toast.error("Failed to load existing value for override.");
      }
    },
    [
      selectedDna,
      filteredData,
      jobNum,
      revMajor,
      revMinor,
      waveNumber,
      seq,
      lotNum,
      originalPartNum,
      isAllowedSupvOverrideUser,
    ]
  );

  const submitSupvOverride = useCallback(
    async (newDbValue) => {
      if (!supvOverrideCtx || !selectedDna) return false;

      try {
        const ok = await api.post("/Inspection/update-mafia-entry", {
          JobNum: String(jobNum),
          DnaNum: String(selectedDna.DNANum),
          RevMajor: Number(revMajor),
          RevMinor: Number(revMinor),
          CellNum: Number(supvOverrideCtx.cellNum),
          EnteredBy: Number(enteredBy || 0),
          WaveNumber: Number(waveNumber),
          PartNum: String(selectedDna.PartNum),
          OrgPartNum: String(originalPartNum),
          Lot: Number(lotNum || 0),
          PieceNumber: Number(supvOverrideCtx.pieceNum),
          NewValue: String(newDbValue),
          OldValue: String(supvOverrideCtx.currentValue),
          Seq: Number(seq),
        });

        const isTrue = ok?.data === true;

        if (isTrue) {
          clearReadCache();
          await refreshCurrentDna(selectedDna);
          refreshFailFlags(); // Round 9: override may add/remove a FAIL
          return true;
        }
        return false;
      } catch (err) {
        // Round 12: backend rejects non-supervisors with 403 + message.
        const msg = err?.response?.data?.error;
        if (msg) toast.error(msg);
        return false;
      }
    },
    [
      supvOverrideCtx,
      selectedDna,
      jobNum,
      revMajor,
      revMinor,
      enteredBy,
      waveNumber,
      originalPartNum,
      lotNum,
      seq,
      refreshCurrentDna,
      clearReadCache,
    ]
  );

  const legacyCloseJobFlow = useCallback(async () => {
    if (readOnly) return; // Round 15: simulator preview
    if (isInProcType) {
      toast.error(
        "Only the Quality Department can close a job. Perhaps you meant to close the OP instead?"
      );
      return;
    }

    if (jobClosed) {
      toast.info("Job is already closed.");
      return;
    }

    setShowConfirmCloseJob(true);
  }, [isInProcType, jobClosed]);

  const doCloseJobAfterConfirm = useCallback(async () => {
    if (closingJob) return;

    setClosingJob(true);
    try {
      const waveNo = 2;
      const lot = Number(lotNum || 0);

      const dnaList = (filteredData || [])
        .map((d) => String(d?.DNANum || "").replace("!", "").trim())
        .filter(Boolean);

      if (dnaList.length === 0) {
        toast.error("No DNA list available to close this job.");
        return;
      }

      const resp = await api.post("/Inspection/prepare-close-job", {
        JobNum: String(jobNum),
        RevMajor: Number(revMajor),
        RevMinor: Number(revMinor),
        WaveNumber: waveNo,
        Seq: Number(seq),
        PartNum: String(partNum),
        LotNum: lot,
        OrgPartNum: String(originalPartNum),
        JobQuan: Number(quantity),
        DnaNums: dnaList,
      });

      const pcsRemaining = Number(resp?.data?.pcsRemaining ?? 0);
      const nonConformities = Number(resp?.data?.nonConformities ?? 0);
      const needsOverride = !!resp?.data?.needsOverride;

      if (needsOverride) {
        setSupvPcsRemaining(pcsRemaining);
        setSupvNonConfs(nonConformities);

        pendingCloseJobRef.current = {
          pcsRemaining,
          nonConformities,
          lot,
        };

        setShowSupvOv(true);
        return;
      }

      const closedByGuess = (() => {
        try {
          const authData = sessionStorage.getItem("authUser");
          if (!authData) return 0;
          const parsed = JSON.parse(authData);
          const empId =
            parsed?.empID ??
            parsed?.empId ??
            parsed?.employeeId ??
            parsed?.employeeID ??
            0;
          const n = Number(empId);
          return Number.isFinite(n) ? n : 0;
        } catch {
          return 0;
        }
      })();

      const ok = await api.post("/Inspection/closejobquality", {
        JobNum: String(jobNum),
        Seq: Number(seq),
        PartNum: String(partNum),
        QualityClosedBy: Number(closedByGuess || 0),
        Noncomformant: false,
        Lot: lot,
        OrgPartNum: String(originalPartNum),
      });

      if (ok?.status === 200 && ok?.data === true) {
        toast.success("Job closed.");
        setJobClosed(true);
        clearReadCache();
      } else {
        toast.error(
          "Error in closing job. Please try again or contact technical support on x1776."
        );
      }
    } catch (err) {
      // Go-live parity (2026-09-24): show the server's reason when present.
      toast.error(err?.response?.data?.error || "Unexpected error while closing job.");
    } finally {
      setClosingJob(false);
    }
  }, [
    filteredData,
    quantity,
    revMajor,
    revMinor,
    partNum,
    jobNum,
    seq,
    lotNum,
    originalPartNum,
    closingJob,
    clearReadCache,
  ]);

  const finalizeCloseJobWithOverride = useCallback(
    async (closedById) => {
      try {
        const pending = pendingCloseJobRef.current;
        if (!pending) {
          toast.error("Close Job data missing. Please try again.");
          return;
        }

        const lot = Number(pending.lot || 0);
        const nonCompliant = true;

        const resp = await api.post("/Inspection/closejobquality", {
          JobNum: String(jobNum),
          Seq: Number(seq),
          PartNum: String(partNum),
          QualityClosedBy: Number(closedById || 0),
          Noncomformant: !!nonCompliant,
          Lot: lot,
          OrgPartNum: String(originalPartNum),
        });

        if (resp?.status === 200 && resp?.data === true) {
          toast.success("Job closed.");
          setJobClosed(true);
          clearReadCache();
        } else {
          toast.error(
            "Error in closing job. Please try again or contact technical support on x1776."
          );
        }
      } catch (err) {
        // Backend returns 400 with a specific error when QualityClosedBy is
        // not a manager (legacy frmSupvOv.IsManager check, restored in
        // InspectionController.CloseJobQuality). Surface the legacy message
        // verbatim instead of the generic fallback.
        const status = err?.response?.status;
        const backendError = err?.response?.data?.error;
        if (status === 400 && backendError) {
          toast.error(backendError);
        } else {
          toast.error("Unexpected error while closing job.");
        }
      } finally {
        pendingCloseJobRef.current = null;
      }
    },
    [jobNum, seq, partNum, originalPartNum, clearReadCache]
  );

  useImperativeHandle(ref, () => ({
    openCloseOp: () => legacyCloseOpFlow(),
    openCloseJob: () => legacyCloseJobFlow(),

    // Round DI-2 (Issues 3/9/18) — used by the page-level Close Inspection
    // flow and the navigation guard: saves the current DNA's entries and
    // comment (same rules the DNA-switch auto-save enforces: skipped
    // entries / format problems / unclassified out-of-spec values all
    // BLOCK, returning false, and nothing entered is lost). Reads through
    // the ref so it always sees the latest state.
    flushAutoSave: () => autoSaveRef.current(),
    hasUnsavedWork: () => {
      try {
        return hasUnsavedWork();
      } catch {
        return false;
      }
    },
    // Round DI-3 (owner): the panel is PERMANENT now and BOTH upload
    // buttons do the same thing — open the file picker directly.
    toggleDocs: () => { setShowDocs(true); openUploadPickerRef.current?.(); },

    canShowCloseOp: !!canShowCloseOp,
    opClosed: !!opClosed,

    addOneMorePiece,
    goPrevDna,
    goNextDna,
    inspectAll: inspectAllToQuantity,
    clearAddedBoxes,
    // Backward-compat alias (pre-Round-9 name); same quantity-fill
    // behavior — the 21-box cap is gone.
    inspectAllTo21: inspectAllToQuantity,
  }));

  const statusBadge = useMemo(() => {
    if (!selectedDna) return null;
    const closed = (isInProcType && opClosed) || (!isInProcType && jobClosed);
    return closed ? (
      <Badge color="danger" pill>
        CLOSED
      </Badge>
    ) : (
      <Badge color="success" pill>
        OPEN
      </Badge>
    );
  }, [selectedDna, isInProcType, opClosed, jobClosed]);

  const canRenderGrid =
    !!selectedDna && (Number(entryCount || 0) > 0 || extraEntries.length > 0);

  return (
    <div className="di-module di-workspace">
      <div className="di-rail">
        <OpNumSelector
          data={fullData}
          selectedOpNum={selectedOpNum}
          onSelect={async (v) => {
            // Round DI-2 (Issue 3) — changing the OPERATION unloads the
            // current DNA grid, so the same auto-save that protects a DNA
            // switch runs first; a blocked save keeps the current Op (and
            // every entered value) with the reason shown.
            if (selectedDnaRef.current) {
              const ok = await autoSaveRef.current();
              if (!ok) return;
            }
            if (typeof v === "number") return setSelectedOpNum(v);
            if (typeof v === "string") {
              const trimmed = v.trim();
              if (/^\d+$/.test(trimmed)) setSelectedOpNum(Number(trimmed));
              else if (trimmed === "All" || trimmed === "Uncategorized")
                setSelectedOpNum(trimmed);
              else setSelectedOpNum(null);
            } else setSelectedOpNum(null);
          }}
        />

        <DnaNumDetails
                failedDnas={failedDnas}
          data={filteredData}
          selectedDna={selectedDna}
          onSelectDna={handleDnaSelect}
        />

        <div className="di-panel">
          <div className="di-panel__head">
            <span>Status</span>
            {statusBadge}
          </div>
          <div className="di-panel__body">
            <div className="di-rail-stats">
              <div className="di-rail-stat">
                <b>{jobQuantity || "—"}</b>
                <span>Job Qty</span>
              </div>
              <div className="di-rail-stat">
                <b>{Number(entryCount || 0) + extraEntries.length}</b>
                <span>Boxes</span>
              </div>
            </div>
            {(loadingMafiaTable || loadingDnaDetails) && (
              <div className="small text-muted mt-2">
                {loadingMafiaTable ? "Loading MAFIA table..." : ""}
                {loadingDnaDetails ? " Loading DNA detail..." : ""}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="di-main">
        {/* Round DI-2.2 (layout fix): DI-1 wrapped this header in a flex
            row to seat the old "Upload Files" button beside it. With that
            button moved to the top action bar, the leftover wrapper shrank
            the header card to min-content width. Rendered directly =
            full-width strip (pre-DI-1 layout). */}
        <DnaDetailsHeader selectedDna={selectedDna} checkPieces={entryCount} />

        <div
          className="di-panel"
          style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
        >
          <div
            className="di-panel__body"
            style={{ overflowY: "auto", flex: 1, minHeight: 0 }}
            data-inspection-scroll
          >
            {!selectedDna ? (
              <div className="text-muted p-3">
                Select an <b>Op</b> and a <b>DNA</b> to start entering readings.
              </div>
            ) : !canRenderGrid ? (
              <div className="text-muted p-3">
                No entry boxes for this DNA yet. Use <b>+</b> or <b>Inspect All</b> to add boxes.
              </div>
            ) : (
              <>
                {isAllowedSupvOverrideUser() ? (
                  <div
                    role="note"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      margin: "0 0 10px",
                      padding: "6px 10px",
                      fontSize: 12.5,
                      color: "#664d03",
                      background: "#fff3cd",
                      border: "1px solid #ffe69c",
                      borderRadius: 8,
                    }}
                  >
                    <i className="ri-shield-keyhole-line" />
                    <span>
                      <strong>Supervisor Override available.</strong>{" "}
                      Double-click a piece&nbsp;# label below to override a
                      recorded value.
                    </span>
                  </div>
                ) : null}
                <DynamicCommentGrid
                  key={selectedDna?.DNANum}
                  count={visibleBase.count || 0}
                  extraEntries={mergedExtraEntries}
                  values={entryValues}
                  onChange={(name, val) =>
                    setEntryValues((prev) => ({ ...prev, [name]: val }))
                  }
                  labels={visibleBase.labels}
                  names={visibleBase.names}
                  disabledFields={[
                    ...new Set([
                      ...(disabledFields || []),
                      ...closedAllDisabledNames,
                      ...savedThisSession,
                    ]),
                  ]}
                  highlightedFields={[
                    ...new Set([...(highlightedFields || []), ...outSpecNames]),
                  ]}
                  overrideEnabled={isAllowedSupvOverrideUser()}
                  onLabelDoubleClick={openSupvOverrideForCell}
                  onFieldBlur={handleFieldBlur}
                  onRemoveBox={readOnly ? undefined : removeBox}
                  onDeleteSaved={readOnly ? undefined : deleteSavedEntry}
                />
              </>
            )}
          </div>

          <div className="di-actionbar">
            {/* Round 4 (comments parity): legacy caption + helper text from
                Inspect.Designer.vb Label8 ("OPERATOR'S COMMENTS FOR CURRENT
                DNA# ..."). The textarea behavior, value (payloadComment) and
                save/load wiring are unchanged — comments are still keyed per
                DNA / wave / job / part / lot exactly as before. */}
            <div className="di-comments" style={{ flex: 1, minWidth: 0 }}>
              <label
                htmlFor="di-operator-comments"
                className="di-comments__label"
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.03em",
                  color: "#495057",
                  marginBottom: 4,
                }}
              >
                OPERATOR'S COMMENTS FOR CURRENT DNA#
                {selectedDna?.DNANum ? ` ${selectedDna.DNANum}` : ""}
              </label>
              <div
                className="di-comments__help"
                style={{
                  fontSize: 11.5,
                  color: "#6c757d",
                  marginBottom: 6,
                  lineHeight: 1.4,
                }}
              >
                General comments, gauge number used, passivation
                time/temperature, weld type, tool used, CNC program used, etc.
              </div>
              {/* Round DI-1/DI-2/DI-2.1 — Date & Initial Stamp.
                  DI-2.1 (owner follow-up: "date and time should come
                  automatically"): the stamp is now inserted AUTOMATICALLY
                  the moment the operator starts typing in the comment box
                  (once per DNA session, at the start of the line being
                  edited — see the textarea onChange below). This button
                  stays for inserting ADDITIONAL stamps manually.
                  Format per owner example: "[2026-07-10 09:42 AS] ".
                  Identity fallback chain when a display name is missing:
                  username → EmpID (clearly labeled) — never hardcoded,
                  never someone else's identity. The stamp marks the comment
                  dirty, so the normal DNA-move auto-save persists it. */}
              {!readOnly ? (
                <button
                  type="button"
                  className="di-btn"
                  style={{ border: "1px solid #d0d5dd", marginBottom: 6 }}
                  onClick={() => {
                    const stampTxt = buildStamp();
                    if (!stampTxt) {
                      toast.error("Could not determine your identity for the stamp. Please log out and back in.");
                      return;
                    }
                    const el = document.getElementById("di-operator-comments");
                    setPayloadComment((prev) => {
                      const text = prev || "";
                      // Insert at the cursor when the textarea has a live
                      // selection; otherwise append to the end.
                      const pos =
                        el && document.activeElement === el && Number.isInteger(el.selectionStart)
                          ? el.selectionStart
                          : text.length;
                      const next =
                        text.slice(0, pos) +
                        (pos > 0 && !/\s$/.test(text.slice(0, pos)) ? "\n" : "") +
                        stampTxt +
                        text.slice(pos);
                      return next;
                    });
                    setCommentDirty(true); // Issue 15 — stamp = dirty now
                    try { el?.focus(); } catch { /* ignore */ }
                  }}
                  title="Insert the current date, time and your initials into the comment (also added automatically when you start typing)"
                >
                  <i className="ri-quill-pen-line" />
                  Date &amp; Initial Stamp
                </button>
              ) : null}
              <textarea
                id="di-operator-comments"
                className="form-control"
                placeholder="Enter operator comments for this DNA..."
                disabled={readOnly}
                value={payloadComment}
                onChange={(e) => {
                  let next = e.target.value;
                  // Round DI-2.1 — AUTOMATIC date/time/initials: on the
                  // FIRST edit of this DNA's comment in this session, the
                  // stamp is inserted at the start of the line being typed
                  // (subsequent edits don't re-stamp; the button adds more
                  // stamps on demand). Cursor is restored right after the
                  // typed character.
                  if (!commentDirty) {
                    const stamp = buildStamp();
                    if (stamp) {
                      const el = e.target;
                      const pos = Number.isInteger(el.selectionStart)
                        ? el.selectionStart
                        : next.length;
                      const lineStart =
                        next.lastIndexOf("\n", Math.max(0, pos - 1)) + 1;
                      next =
                        next.slice(0, lineStart) + stamp + next.slice(lineStart);
                      const newPos = pos + stamp.length;
                      requestAnimationFrame(() => {
                        try {
                          el.setSelectionRange(newPos, newPos);
                        } catch {}
                      });
                    }
                  }
                  setPayloadComment(next);
                  setCommentDirty(true);
                }}
                rows={2}
              />
            </div>

            {unresolvedCells.length > 0 && (
              <button
                type="button"
                className="di-btn di-btn--danger-ghost"
                onClick={() => {
                  setProblemsList(buildProblemMessages(unresolvedCells));
                  setShowProblemsModal(true);
                }}
              >
                <i className="ri-error-warning-line"></i>
                Review Issues
              </button>
            )}

            {/* Cleanup: reverse Inspect All — clear all ADDED boxes at once.
                Only shown when there are added boxes to clear. */}
            {unsavedExtraCount > 0 && !readOnly ? (
              <button
                type="button"
                className="di-btn"
                style={{ border: "1px solid #f5c2c7", color: "#b02a37" }}
                onClick={clearAddedBoxes}
                title="Remove all unsaved added (+) boxes; saved boxes are kept"
              >
                <i className="ri-eraser-line" />
                Clear added ({unsavedExtraCount})
              </button>
            ) : null}

            {/* Cleanup: Documents toggle. Opens the per-job attachments
                panel on demand instead of always showing it at the bottom. */}
            {/* Round DI-2 (Issue 4) — the "Upload File" button lives in the
                PAGE-LEVEL top action area; it opens this panel through the
                toggleDocs ref method. */}

            {/* Round 5: explicit Supervisor Override button (permission
                holders only). Opens a small picker of the current DNA's
                pieces, then reuses the same openSupvOverrideForCell flow
                as double-click. */}
            {isAllowedSupvOverrideUser() && !readOnly ? (
              <div style={{ position: "relative" }}>
                <button
                  type="button"
                  className="di-btn"
                  style={{
                    border: "1px solid #f0ad4e",
                    color: "#b8860b",
                    background: "#fff",
                  }}
                  onClick={() => setShowOvrPicker((v) => !v)}
                  title="Supervisor Override a recorded piece value"
                >
                  <i className="ri-shield-keyhole-line"></i>
                  Supervisor Override
                </button>

                {showOvrPicker ? (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "calc(100% + 6px)",
                      right: 0,
                      zIndex: 3000,
                      minWidth: 220,
                      maxHeight: 260,
                      overflowY: "auto",
                      background: "#fff",
                      border: "1px solid #e9ecef",
                      borderRadius: 8,
                      boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                      padding: 6,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: "#6c757d",
                        padding: "4px 8px",
                      }}
                    >
                      Override which piece?
                    </div>
                    {(visibleBase.names || []).length === 0 ? (
                      <div style={{ padding: "6px 8px", fontSize: 12.5, color: "#6c757d" }}>
                        No pieces to override.
                      </div>
                    ) : (
                      (visibleBase.names || []).map((nm, i) => (
                        <button
                          key={nm}
                          type="button"
                          className="dropdown-item"
                          style={{
                            width: "100%",
                            textAlign: "left",
                            padding: "6px 8px",
                            border: 0,
                            background: "transparent",
                            cursor: "pointer",
                            borderRadius: 6,
                          }}
                          onClick={() => {
                            setShowOvrPicker(false);
                            openSupvOverrideForCell(nm, visibleBase.labels?.[i]);
                          }}
                        >
                          Piece <strong>{visibleBase.labels?.[i] ?? i + 1}</strong>
                        </button>
                      ))
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Round DI-1/DI-2 — Save Entries button removed: entries and
                the comment AUTO-SAVE when the operator moves to the next
                DNA (see handleDnaSelect). Status pill: Saving… / Saved /
                Save failed (Issue 3). */}
            {savingEntries ? (
              <span className="di-btn" style={{ pointerEvents: "none", opacity: 0.8 }}>
                <i className="ri-save-line"></i> Saving…
              </span>
            ) : lastSaveStatus === "saved" ? (
              <span
                className="di-btn"
                style={{ pointerEvents: "none", color: "#0f5132", border: "1px solid #badbcc", background: "#d1e7dd" }}
              >
                <i className="ri-check-line"></i> Saved
              </span>
            ) : lastSaveStatus === "failed" ? (
              <span
                className="di-btn"
                style={{ pointerEvents: "none", color: "#842029", border: "1px solid #f5c2c7", background: "#f8d7da" }}
                title="The last auto-save failed — your entries were kept on screen. Fix any highlighted issues and move DNA again to retry."
              >
                <i className="ri-error-warning-line"></i> Save failed
              </span>
            ) : null}
          </div>

          {/* Round DI-3 (owner): the per-job documents panel is ALWAYS
              visible; the page-level "Upload File" button and the panel's
              own Upload button both open the file picker directly. */}
          {jobNum ? (
            <AttachmentsPanel
              jobNum={String(jobNum)}
              assemblySeq={Number(seq) || 0}
              partNum={selectedDna?.PartNum || partNum || ""}
              lot={Number(lotNum) || 0}
              disabled={readOnly}
              registerOpenPicker={(fn) => { openUploadPickerRef.current = fn; }}
            />
          ) : null}
        </div>
      </div>

      {showOutOfSpecModal && (
        <OutOfSpecModal
          busy={savingEntries}
          epicorDmrEnabled={epicorDmrEnabled}
          onClose={() => setShowOutOfSpecModal(false)}
          onConfirm={async (info) => {
            // UAT Tests 14-16: the modal closes ONLY when the save
            // actually succeeds. On failure the server error is shown
            // (toast) and the modal + inspection context stay intact so
            // nothing is lost and the workspace never goes blank.
            const ok = await proceedToSave({
              cellNums: unresolvedCells,
              ...info,
            });
            if (ok) setShowOutOfSpecModal(false);
          }}
        />
      )}

      {/* Close Op confirm */}
      <ConfirmModal
        open={showConfirmCloseOp}
        title="Please Confirm"
        message={confirmCloseOpMsg}
        onCancel={() => setShowConfirmCloseOp(false)}
        onConfirm={() => {
          setShowConfirmCloseOp(false);
          setShowCloseOpModal(true);
        }}
      />

      <CloseOpModal
        open={showCloseOpModal}
        onCancel={() => setShowCloseOpModal(false)}
        onConfirm={onConfirmCloseOp}
        jobNum={jobNum}
        opNum={typeof selectedOpNum === "number" ? selectedOpNum : undefined}
        totalPieces={jobQuantity}
      />

      {/* Close Job confirm */}
      <ConfirmModal
        open={showConfirmCloseJob}
        title="Confirm"
        message="This action will finalize the job and prevent any further entries to be made to the q-final readings. Do you wish to continue?"
        onCancel={() => setShowConfirmCloseJob(false)}
        onConfirm={async () => {
          setShowConfirmCloseJob(false);
          await doCloseJobAfterConfirm();
        }}
      />

      {/* Supervisor Override (frmSupvOv) — NO password verification.
          Go-live parity (2026-09-24): the override is authorized and
          recorded for the SIGNED-IN user (server-enforced), shown here. */}
      <FrmSupvOv
        open={showSupvOv}
        signedInEmpId={enteredBy}
        pcsRemaining={supvPcsRemaining}
        nonConformities={supvNonConfs}
        onCancel={() => {
          setShowSupvOv(false);
          pendingCloseJobRef.current = null;
          toast.info("Operation aborted - job not closed.");
        }}
        onConfirm={(closedById) => {
          setShowSupvOv(false);
          finalizeCloseJobWithOverride(closedById);
        }}
      />

      <ProblemsModal
        open={showProblemsModal}
        problems={problemsList}
        onClose={() => setShowProblemsModal(false)}
      />

      <SupvOverrideModal
        open={showSupvOverrideModal}
        context={supvOverrideCtx}
        onClose={() => {
          setShowSupvOverrideModal(false);
          setSupvOverrideCtx(null);
        }}
        onConfirm={submitSupvOverride}
      />
    </div>
  );
};

const InspectionSection = forwardRef(InspectionSectionInner);
export default InspectionSection;